import type { RouteRecordRaw, Router } from 'vue-router'
import { useAuthStore } from '@/modules/Auth/store'
import { MODULE_PERMISSIONS } from '@/modules/Core/constants/adminAccess'
import { HrPermission } from '@/modules/Core/constants/hrPermissions'
import { canAccessPermissions } from '@/modules/Core/utils/permissions'
const ownEmploymentFormMeta = {
  requiredPermissions: [...MODULE_PERMISSIONS.employmentForm],
  ownEmploymentForm: true,
}

const employmentFormHrViewMeta = {
  requiredPermissions: [...MODULE_PERMISSIONS.employmentFormHrView],
}

const recruitmentModulePermissions = [
  HrPermission.EmployeeReadApplicant,
  HrPermission.FormCreate,
  HrPermission.FormUpdate,
  HrPermission.FormReadApplication,
  HrPermission.FormReadAll,
  HrPermission.FormSubmissionCreate,
  HrPermission.FormSubmissionReadOwn,
  HrPermission.FormSubmissionReadAll,
] as const

function resolveRecruitmentModuleRedirect() {
  const authStore = useAuthStore()

  if (
    canAccessPermissions(
      authStore.permissions,
      MODULE_PERMISSIONS.recruitmentApplicants,
      'any'
    )
  ) {
    return { name: 'ManageApplicantsRoute' }
  }

  if (
    canAccessPermissions(
      authStore.permissions,
      MODULE_PERMISSIONS.recruitmentFormList,
      'any'
    )
  ) {
    return { name: 'EmploymentFormsListRoute' }
  }

  return { name: 'home' }
}

const RecruitmentRoutes: RouteRecordRaw = {
  path: '/recruitment',
  name: 'RecruitmentModulePages',
  redirect: resolveRecruitmentModuleRedirect,
  children: [
    {
      path: 'applicants',
      name: 'ManageApplicantsRoute',
      component: () => import('./views/ManageApplicantsView.vue'),
      meta: {
        title: 'متقاضیان',
        requiredPermissions: [...MODULE_PERMISSIONS.recruitmentApplicants],
      },
    },
    {
      path: 'form-builder',
      name: 'EmploymentFormsListRoute',
      component: () => import('./views/ManageEmploymentFormsView.vue'),
      meta: {
        title: 'فرم ساز',
        requiredPermissions: [...MODULE_PERMISSIONS.recruitmentFormList],
      },
    },
    {
      path: 'form-builder/create',
      name: 'EmploymentFormCreateRoute',
      component: () => import('./views/EmploymentFormBuilderEditorView.vue'),
      meta: {
        title: 'ایجاد فرم استخدام',
        requiredPermissions: [...MODULE_PERMISSIONS.recruitmentFormCreate],
      },
    },
    {
      path: 'form-builder/create/preview',
      name: 'EmploymentFormCreatePreviewRoute',
      component: () => import('./views/FormBuilderPreviewView.vue'),
      meta: {
        title: 'پیش‌نمایش فرم',
        pageHasBack: true,
        previewFallback: 'EmploymentFormsListRoute',
        requiredPermissions: [...MODULE_PERMISSIONS.recruitmentFormCreate],
      },
    },
    {
      path: 'form-builder/:formId/preview',
      name: 'EmploymentFormEditPreviewRoute',
      component: () => import('./views/FormBuilderPreviewView.vue'),
      meta: {
        title: 'پیش‌نمایش فرم',
        pageHasBack: true,
        previewFallback: 'EmploymentFormsListRoute',
        requiredPermissions: [...MODULE_PERMISSIONS.recruitmentFormEdit],
      },
    },
    {
      path: 'form-builder/:formId/edit',
      name: 'EmploymentFormEditRoute',
      component: () => import('./views/EmploymentFormBuilderEditorView.vue'),
      meta: {
        title: 'ویرایش فرم استخدام',
        requiredPermissions: [...MODULE_PERMISSIONS.recruitmentFormEdit],
      },
    },
    {
      path: 'employment-form',
      name: 'EmploymentFormEntryRoute',
      component: () => import('./views/EmploymentFormView.vue'),
      meta: {
        title: 'فرم استخدامی',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/personal',
      name: 'EmploymentFormOwnPersonalStepRoute',
      component: () => import('./views/EmploymentFormPersonalStepView.vue'),
      meta: {
        title: 'اطلاعات هویتی و شخصی',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/identity-info',
      name: 'EmploymentFormOwnIdentityInfoStepRoute',
      component: () => import('./views/EmploymentFormIdentityInfoStepView.vue'),
      meta: {
        title: 'اطلاعات شناسنامه‌ای',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/family-info',
      name: 'EmploymentFormOwnFamilyInfoStepRoute',
      component: () => import('./views/EmploymentFormFamilyInfoStepView.vue'),
      meta: {
        title: 'اطلاعات خانواده و بستگان',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/military-service',
      name: 'EmploymentFormOwnMilitaryServiceStepRoute',
      component: () =>
        import('./views/EmploymentFormMilitaryServiceStepView.vue'),
      meta: {
        title: 'اطلاعات نظام وظیفه',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/educational-info',
      name: 'EmploymentFormOwnEducationalInfoStepRoute',
      component: () =>
        import('./views/EmploymentFormEducationalInfoStepView.vue'),
      meta: {
        title: 'اطلاعات تحصیلی',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/work-experience',
      name: 'EmploymentFormOwnWorkExperienceStepRoute',
      component: () =>
        import('./views/EmploymentFormWorkExperienceStepView.vue'),
      meta: {
        title: 'سوابق شغلی (اختیاری)',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/dynamic/:formId',
      name: 'EmploymentFormOwnDynamicStepRoute',
      component: () => import('./views/EmploymentFormDynamicStepView.vue'),
      meta: {
        title: 'فرم استخدامی',
        ...ownEmploymentFormMeta,
      },
    },
    {
      path: 'employment-form/:employeeId',
      name: 'EmploymentFormRoute',
      component: () => import('./views/EmploymentFormView.vue'),
      meta: {
        title: 'فرم استخدامی',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/personal',
      name: 'EmploymentFormPersonalStepRoute',
      component: () => import('./views/EmploymentFormPersonalStepView.vue'),
      meta: {
        title: 'اطلاعات هویتی و شخصی',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/identity-info',
      name: 'EmploymentFormIdentityInfoStepRoute',
      component: () => import('./views/EmploymentFormIdentityInfoStepView.vue'),
      meta: {
        title: 'اطلاعات شناسنامه‌ای',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/family-info',
      name: 'EmploymentFormFamilyInfoStepRoute',
      component: () => import('./views/EmploymentFormFamilyInfoStepView.vue'),
      meta: {
        title: 'اطلاعات خانواده و بستگان',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/military-service',
      name: 'EmploymentFormMilitaryServiceStepRoute',
      component: () =>
        import('./views/EmploymentFormMilitaryServiceStepView.vue'),
      meta: {
        title: 'اطلاعات نظام وظیفه',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/educational-info',
      name: 'EmploymentFormEducationalInfoStepRoute',
      component: () =>
        import('./views/EmploymentFormEducationalInfoStepView.vue'),
      meta: {
        title: 'اطلاعات تحصیلی',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/work-experience',
      name: 'EmploymentFormWorkExperienceStepRoute',
      component: () =>
        import('./views/EmploymentFormWorkExperienceStepView.vue'),
      meta: {
        title: 'سوابق شغلی (اختیاری)',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form/:employeeId/dynamic/:formId',
      name: 'EmploymentFormDynamicStepRoute',
      component: () => import('./views/EmploymentFormDynamicStepView.vue'),
      meta: {
        title: 'فرم استخدامی',
        ...employmentFormHrViewMeta,
      },
    },
    {
      path: 'employment-form-legacy/:applicantId',
      meta: {
        ...employmentFormHrViewMeta,
      },
      redirect: (to) => ({
        name: 'EmploymentFormRoute',
        params: { employeeId: to.params.applicantId },
      }),
    },
  ],
  meta: {
    layout: 'Default',
    requiredPermissions: [...recruitmentModulePermissions],
  },
}

export default (router: Router) => {
  router.addRoute(RecruitmentRoutes)
}
